import { Box } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog/Dialog";
import Grid from "@material-ui/core/Grid";
import CloseIcon from "@material-ui/icons/Close";
import { AxiosResponse } from "axios";
import "handsontable/dist/handsontable.full.css";
import React, {
  forwardRef,
  MouseEventHandler,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import HotTable, { HotColumn } from "@handsontable/react";
import Handsontable, { CellCoords } from "handsontable";
import IndividualTestResultDTO from "../../../../../../../api/models/test-result/IndividualTestResultDTO";
import TestResultRowDTO from "../../../../../../../api/models/test-result/TestResultRowDTO";
import { TestCaseSearchResult } from "../../../../../../../api/models/test-series/TestCaseSearchResult";
import AttachmentApi from "../../../../../../../api/services/AttachmentApi";
import TestRequestApi from "../../../../../../../api/services/TestRequestApi";
import CustomProgress from "../../../../../../../components/Alert/CustomProgress";
import ErrorMessage, { ErrorMessageRef } from "../../../../../../../components/Alert/ErrorSnackbar/ErrorMessage";
import CustomButton from "../../../../../../../components/Button/CustomButton";
import CustomLinkButton from "../../../../../../../components/Button/CustomLinkButton";
import BlackChipTooltip from "../../../../../../../components/Chip/BlackChipTooltip";
import AttachmentDetailDialog from "../../../../../../../components/Dialog/AttachmentDialog/AttachmentDetailDialog";
import ConfirmationDialogContext from "../../../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import AttachmentDialog from "../../../../../../../components/Gallery/AttachmentDialog";
import FileReference from "../../../../../../../components/Gallery/model/FileReference.model";
import { DOCUMENT, PICTURE } from "../../../../../../../components/Gallery/model/GalleryFlag.model";
import LoadedFile from "../../../../../../../components/Gallery/model/LoadedFile.model";
import ErrorSnackbarContext from "../../../../../../../contexts/ErrorSnackbarContext";
import { TestRequestContext } from "../../../../../../../contexts/TestRequestContext";
import AttachmentFileHandler from "../../../../../../../helper/AttachmentFileHandler";
import FileNameHelper from "../../../../../../../helper/FileNameHelper";
import UploadHelper from "../../../../../../../helper/UploadHelper";
import useEnv from "../../../../../../../hooks/env";
import { tesaDarkRedError } from "../../../../../../../styles/colors";
import TestCaseRating from "../../../../../models/TestCaseRating.model";
import FileUploadRendererComponent from "../FileUploadRenderer";
import FileViewRendererComponent from "../FileViewRenderer";
import { NoteEditorComponent } from "../NoteEditor";
import NoteRendererComponent from "../NoteRenderer";
import TestCaseRatingRadioGroup from "../TestCaseRatingRadioGroup";
import { FilterableColumn } from "../../interfaces/FilterableColumn";
import useStyles from "./TestCasesParametersTableStyles";
import ParameterValueDTO from "../../../../../../../api/models/test-result/ParameterValueDTO";
import getTextWidth from "../../utils/GetTextWidth";
import ConfidentialConfirmationDialog from "../../../../../../../components/Dialog/ConfidentialConfirmationDialog";
import {
  containsHighlyConfidentialInfo,
  fetchHighlyConfidentialRegex,
} from "../../../../../../../functions/confidentialCheck.function";

Handsontable.cellTypes.registerCellType("voided", {
  editor: Handsontable.editors.CheckboxEditor,
  renderer: Handsontable.renderers.CheckboxRenderer,
});

function isNavigationKey(key: string): boolean {
  return ["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown", "Tab"].includes(key);
}

export interface HeaderType<T> {
  id: T;
  name: string;
  tooltip?: {
    section: string;
    chipSelector: string;
    subHeader?: string;
  };
  filterableColumnId?: FilterableColumn;
}

interface TestCasesParametersTableProps {
  cursor: number;
  testCase: TestCaseSearchResult;
  testSeriesStatus: string;
  open: boolean;
  readOnly: boolean;
  onClose: () => void;
  handleLeftButtonClick: MouseEventHandler<HTMLButtonElement>;
  handleRightButtonClick: MouseEventHandler<HTMLButtonElement>;
  row: any;
  headerArray: HeaderType<string>[];
  testSeriesObject: any;
}

export interface TestCasesParametersTableRef {
  saveCurrent: () => Promise<void>;
}

function TestCasesParametersTable(
  props: TestCasesParametersTableProps,
  ref: Ref<TestCasesParametersTableRef> | undefined,
) {
  const classes = useStyles(props);
  const { api } = useEnv();
  const testRequestService: TestRequestApi = new TestRequestApi(api);
  const attachmentApi = new AttachmentApi(api);
  const uploadHelper = new UploadHelper(attachmentApi);
  const hot = React.useRef<HotTable | null>(null);
  const errorMessage = React.useRef<ErrorMessageRef | null>(null);

  const [tableColumns, setTableColumns] = useState<TestResultRowDTO[] | undefined>([]);
  const [apiData, setApiData] = useState<IndividualTestResultDTO | null>(null);
  const [tableRows, setTableRows] = useState([] as any[]);
  const [tableRowsDirtyReference, setTableRowsDirtyReference] = useState([] as any[]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddRowDisabled, setIsAddRowDisabled] = useState(false);
  const [testCaseId, setTestCaseId] = useState(0);
  const { handleHttpError, showErrorSnackbar, showSnackbar } = useContext(ErrorSnackbarContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { testRequestObj } = useContext(TestRequestContext);
  const isDirty = useRef(false);

  const hotSettings = {
    autoRowSize: false,
    autoColumnSize: false,
  };

  const {
    testCase,
    cursor,
    open,
    readOnly,
    onClose,
    row,
    handleLeftButtonClick,
    handleRightButtonClick,
    headerArray,
    testSeriesObject,
    testSeriesStatus,
  } = props;

  const columnCount = tableColumns?.[0]?.parameterValues?.length || 0;
  const noteColumnIndex = columnCount;
  const viewColumnIndex = columnCount + 2;
  const addColumnIndex = readOnly ? -1 : columnCount + 3;
  const testCaseIndexNumber = testCase?.indexNumber || 0;
  const filePrefix = `${testRequestObj.requestId}_${testSeriesObject.indexNumber}_${testCaseIndexNumber}`;
  const completedOrCanceled = testSeriesStatus === "COMPLETED" || testSeriesStatus === "CANCELED";

  const [selection] = useState({ row: 0, column: 0 });
  const changeSelection = (selectedRow: number, selectedColumn: number) => {
    selection.row = selectedRow;
    selection.column = selectedColumn;
  };

  const focusOnHot = () => {
    setTimeout(() => {
      hot.current?.hotInstance?.selectCell(selection.row, selection.column);
      hot.current?.hotInstance?.validateCells();
    }, 100);
  };

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [images, setImages] = useState<LoadedFile[]>([]);
  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<{ files: File[]; description: string } | null>(null);
  const [confidentialRegex, setConfidentialRegex] = useState<RegExp | null>(null);
  const [showConfidentialDialogForSave, setShowConfidentialDialogForSave] = useState(false);
  const [pendingSaveResolve, setPendingSaveResolve] = useState<(() => void) | null>(null);
  const [pendingSaveReject, setPendingSaveReject] = useState<((error: any) => void) | null>(null);

  const onDeleteImage = (fileId: string) => {
    apiData?.testResultRows?.forEach((testResultRow) => {
      const containsImage = testResultRow.attachments.map((attachment) => attachment.uuid).indexOf(fileId);
      if (containsImage >= 0) {
        testResultRow.attachments.splice(containsImage, 1);
      }
    });
    const containsImage = images.map((file) => file.id).includes(fileId);
    if (images.length === 1 && containsImage) {
      hot.current?.hotInstance?.setDataAtCell(selection.row, selection.column, false);
      focusOnHot();
      setShowViewDialog(false);
    }
    setImages((prevState) => {
      const index = prevState.map((file) => file.id).indexOf(fileId);
      if (index >= 0) {
        const stateCopy = [...prevState];
        stateCopy.splice(index, 1);
        return stateCopy;
      }
      return prevState;
    });
  };

  const onDescriptionChange = (id: string, newDescription: string) => {
    setImages((prevState) => {
      const stateCopy = [...prevState];
      const index = stateCopy.map((file) => file.id).indexOf(id);
      if (index >= 0) {
        stateCopy.splice(index, 1, {
          ...stateCopy[index],
          description: newDescription,
        } as LoadedFile);
      }
      return stateCopy;
    });
  };

  const fileName = (file: LoadedFile): string => {
    const index = images.map((image) => image.id).indexOf(file.id);
    return FileNameHelper.postfixFileBeforeEnding(index < 0 ? "" : `-${index + 1}`, (file as LoadedFile).name);
  };

  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) => {
        if (!apiData) return Promise.resolve({ attachments: [], idToBeDeleted });
        const testResultId = apiData.id;
        const testResultRowId = apiData.testResultRows[selection.row].id;
        return testRequestService
          .removeAttachmentFromTestResultRow(testResultId, testResultRowId, idToBeDeleted)
          .then((attachments) => ({
            attachments,
            idToBeDeleted,
          }));
      },
      append: (idsToBeAdded, flag) => {
        if (!apiData) return Promise.resolve([]);
        const testResultId = apiData.id;
        const testResultRowId = apiData.testResultRows[selection.row].id;
        return testRequestService.addAttachmentToTestResultRow(
          testResultId,
          testResultRowId,
          (flag?.type || "DOCUMENT") as "PICTURE" | "DOCUMENT",
          idsToBeAdded,
        );
      },
      zipFileName(files: FileReference[]): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return fileName(file as LoadedFile);
          }
          return file.id;
        }
        return `LEAP${filePrefix}_${selection.row + 1}.zip`;
      },
      fileName,
    },
    () => testRequestService.getTestRequestAttachmentClearanceToken(testRequestObj.requestId).then((res) => res.data),
  );

  const namingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY REPETITION ${selection.row + 1} of TEST ID ${testCaseIndexNumber}`,
        subheader: file.name,
      };
    },
  };

  function generateHotColumns(testResultRow: TestResultRowDTO[] | undefined) {
    let hotColumns: (JSX.Element | undefined)[] | undefined;
    if (testResultRow && testResultRow[0]) {
      // add columns according to amount of measurements, given by the api
      hotColumns = testResultRow[0].parameterValues?.map((parameterValue: any) => {
        let parameterName = parameterValue.parameter.name;
        const columnWidth = getTextWidth(parameterName!);

        if (parameterValue.unitCode) {
          parameterName += `<br> [${parameterValue.unitCode}]`;
        }

        if (parameterValue.parameter.type === "TEXT") {
          return (
            <HotColumn
              key={parameterValue.parameter.id}
              title={parameterName}
              data={parameterValue.parameter.id}
              width={columnWidth}
            />
          );
        }
        if (parameterValue.parameter.type === "NUMBER") {
          return (
            <HotColumn
              key={parameterValue.parameter.id}
              title={parameterName}
              type="numeric"
              data={parameterValue.parameter.id}
              width={columnWidth}
            />
          );
        }
        if (parameterValue.parameter.type === "ENUM") {
          const escapedValues = parameterValue.parameter.values.map(escapeRegExp);
          return (
            <HotColumn
              key={parameterValue.parameter.id}
              title={parameterName}
              type="dropdown"
              validator={new RegExp(`^(${escapedValues.join("|")}|)$`)}
              data={parameterValue.parameter.id}
              source={parameterValue.parameter.values}
              width={columnWidth}
            />
          );
        }
      });
    }
    return hotColumns;
  }

  // Escape regex special characters in ENUM values
  function escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // generates the API data and maps it to the handsontable row
  function generateTableRowsTemplate(testResultRow: TestResultRowDTO[]) {
    const rows = [] as TestResultRowDTO[];

    // add columns according to amount of measurements given by the api
    testResultRow.forEach((result: TestResultRowDTO, i: number) => {
      const row: any = {};
      result.parameterValues.forEach((parameterValue) => {
        // ID used below in value.parameter.id is used to map parameter to correct place in hands on table
        let value;
        if (parameterValue.type === "NUMBER") {
          value = parameterValue.value || parameterValue.value === 0 ? parameterValue.value : "";
        } else {
          value = parameterValue.value ? parameterValue.value : "";
        }

        // at this point parameter must always has an id
        row[parameterValue.parameter.id!] = value;
      });

      // add columns for test repetition voiding and note
      row.note = JSON.stringify({
        repetitionNumber: i + 1,
        testResultRowId: result.id,
        text: result.note,
      });

      row.voided = !result.voided ? result.voided : true;
      row.attachments = result.attachments?.length || 0;
      rows.push(row);
    });
    return rows;
  }

  function updateTestRowApiProp() {
    if (!apiData) return;

    const newTestResultRows = [...apiData.testResultRows];
    // iterate over 1st table matrix dimension, which equals rows
    apiData.testResultRows.forEach((r: TestResultRowDTO, i: number) => {
      // fetch hands on table row data for row i
      const tableRow = tableRows[i];

      // iterate over 2nd Dimension, which equals cells
      r.parameterValues.forEach((cell: ParameterValueDTO, j: number) => {
        // id for parameterValueTableRow from API equals key-value of columns values in Hands on table

        // get Cell value in hands on table
        // at this point parameter must always has an id
        const tableValue = tableRow[cell.parameter.id!];

        // set api data to table value that corresponds to the table position
        cell.value = tableValue === "" ? null : tableValue;

        newTestResultRows[i].parameterValues[j].value = tableValue;
      });
      const noteValue = tableRow.note ? (JSON.parse(tableRow.note) as { text: string }) : { text: "" };
      newTestResultRows[i].note = noteValue.text;
      newTestResultRows[i].voided = tableRow.voided;
    });
    return newTestResultRows;
  }

  // METHODS
  // basically the same as the update function without changing data
  // only flags if the data is different in the table and the api
  const setDirtyIfChanged = (tableData: any[], apiDataArg: IndividualTestResultDTO | null) => {
    if (apiDataArg) {
      // iterate over 1st table matrix dimension, which equals rows
      apiDataArg.testResultRows.forEach((resultRow: TestResultRowDTO, i: number) => {
        // fetch hands on table row data for row i
        const tableRow = tableData[i];
        const tableSingleRowDirtyReference = tableRowsDirtyReference[i];

        // iterate over 2nd Dimension, which equals cells
        resultRow.parameterValues.forEach((cell) => {
          // id for parameterValueTableRow from API equals key-value of columns values in Hands on table

          // get cell value in handsontable
          // at this point parameter must always has an id
          const tableValue = tableRow[cell.parameter.id!];
          // at this point parameter must always has an id
          const tableValueDirtyReference = tableSingleRowDirtyReference[cell.parameter.id!];

          if (tableValue !== tableValueDirtyReference) {
            isDirty.current = true;
          }
        });

        if (tableSingleRowDirtyReference.note !== tableRow.note) {
          isDirty.current = true;
        }

        if (tableSingleRowDirtyReference.voided !== tableRow.voided) {
          isDirty.current = true;
        }
      });
    }
  };

  const addTableRow = () => {
    if (!apiData) return;
    setIsAddRowDisabled(true);
    testRequestService
      .addRepetition(row.id)
      .then((result) => {
        const newResultRows = result.data.testResultRows;
        if (newResultRows && newResultRows.length > 0) {
          const oldTestResultRows = updateTestRowApiProp();
          const newRow = newResultRows[newResultRows.length - 1];
          const newApiData: IndividualTestResultDTO = {
            ...apiData,
            testResultRows: [...(oldTestResultRows || []), newRow],
          };
          setApiData(newApiData);
          const { testResultRows } = newApiData;
          setTableColumns(testResultRows);
          setTableRows(generateTableRowsTemplate(testResultRows));
          setTableRowsDirtyReference(generateTableRowsTemplate(testResultRows));
        }
      })
      .finally(() => {
        focusOnHot();
        setIsAddRowDisabled(false);
      });
  };

  const loadNext = (): Promise<AxiosResponse<IndividualTestResultDTO>> => {
    return testRequestService.getTestResultsByTestCaseId(testCase?.id);
  };

  const save = (): Promise<void> => {
    if (apiData) {
      if (testSeriesStatus !== "COMPLETED" && testSeriesStatus !== "CANCELED") {
        const testResultRows = updateTestRowApiProp() || [];
        
        // Check for confidential information in all notes
        const allNotes = testResultRows.map(row => row.note).filter((note): note is string => Boolean(note));
        if (confidentialRegex && allNotes.length > 0 && containsHighlyConfidentialInfo(allNotes, confidentialRegex)) {
          return new Promise<void>((resolve, reject) => {
            setPendingSaveResolve(() => resolve);
            setPendingSaveReject(() => reject);
            setShowConfidentialDialogForSave(true);
          });
        }
        
        setIsSaving(true);
        return testRequestService
          .updateTestResults({
            ...apiData,
            testResultRows,
          })
          .then(() => {
            apiData.testResultRows = testResultRows;
          })
          .finally(() => {
            isDirty.current = false;
            setIsSaving(false);
          });
      }
    }
    return Promise.resolve();
  };

  const finishEditing = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      const activeEditor = hot.current?.hotInstance?.getActiveEditor();
      if (activeEditor && activeEditor.prop !== "voided") {
        activeEditor.finishEditing(false, false, resolve);
      } else {
        resolve();
      }
    });
  };

  const saveCurrent = (): Promise<void> => {
    errorMessage.current?.hide();
    return finishEditing()
      .then(save)
      .catch((error) => {
        errorMessage.current?.show("Fill the cells correctly");
        focusOnHot();
        throw error;
      });
  };

  useImperativeHandle(ref, () => ({
    saveCurrent,
  }));

  const handleClose = (): void => {
    setDirtyIfChanged(tableRows, apiData);
    if (isDirty.current) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the test results will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      })
        .then((action) => {
          if (action === "DEFAULT") {
            saveCurrent()
              .then(() => {
                onClose();
              })
              .catch(() => {
                focusOnHot();
              });
          } else {
            if (apiData) {
              setTableRows(generateTableRowsTemplate(apiData.testResultRows));
            }
            onClose();
          }
        })
        .catch(() => {
          focusOnHot();
        });
    } else {
      onClose();
    }
  };

  const handleRatingChange = (rating: TestCaseRating) => {
    setApiData((prev) => prev && { ...prev, rating });
    isDirty.current = true;
  };

  const handleBeforeKeyDownEvent = (event: KeyboardEvent): void => {
    if (event.altKey) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    if ((event.key === "Delete" || event.key === "Backspace") && selection.column === noteColumnIndex) {
      event.stopImmediatePropagation();
    }
    if (selection.column === addColumnIndex) {
      if (!isNavigationKey(event.key)) {
        event.stopImmediatePropagation();
      }
      if (event.key === "Enter") {
        event.stopImmediatePropagation();
        hot.current?.hotInstance?.deselectCell();
        setShowUploadDialog(true);
      }
    }
    if (selection.column === viewColumnIndex) {
      if (!isNavigationKey(event.key)) {
        event.stopImmediatePropagation();
      }
      if (event.key === "Enter") {
        event.stopImmediatePropagation();
        const { attachments } = apiData?.testResultRows[selection.row] || {};
        if (attachments && attachments.length > 0) {
          attachmentFileHandler.load(attachments).then((files) => {
            hot.current?.hotInstance?.deselectCell();
            setImages(files);
            setShowViewDialog(true);
          });
        }
      }
    }
  };

  const handleAfterOnCellMouseDown = (event: MouseEvent, coords: CellCoords): void => {
    if (coords.col === noteColumnIndex) {
      setTimeout(() => {
        const activeEditor = hot.current?.hotInstance?.getActiveEditor();
        const dataAtCell = hot.current?.hotInstance?.getDataAtCell(coords.row, coords.col);
        activeEditor?.setValue(dataAtCell);
        activeEditor?.beginEditing(null, event);
      }, 100);
    }
  };

  const beforeOnCellMouseDown = (event: MouseEvent, coords: CellCoords): void => {
    if (coords.col === addColumnIndex) {
      event.stopImmediatePropagation();
      changeSelection(coords.row, coords.col);
      hot.current?.hotInstance?.deselectCell();
      setShowUploadDialog(true);
    }
    if (coords.col === viewColumnIndex) {
      event.stopImmediatePropagation();
      changeSelection(coords.row, coords.col);
      hot.current?.hotInstance?.selectCell(coords.row, coords.col);
      const { attachments } = apiData?.testResultRows[coords.row] || {};
      if (attachments && attachments.length > 0) {
        attachmentFileHandler
          .load(attachments)
          .then((files) => {
            hot.current?.hotInstance?.deselectCell();
            setImages(files);
            setShowViewDialog(true);
          })
          .catch((error) => {
            handleHttpError(error);
          });
      }
    }
  };

  useEffect(() => {
    fetchHighlyConfidentialRegex().then(setConfidentialRegex);
  }, []);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      loadNext()
        .then((result) => {
          const newApiData = result.data;
          const { testResultRows } = newApiData;
          setApiData(newApiData);
          setTableColumns(testResultRows);
          setTableRows(generateTableRowsTemplate(testResultRows));
          setTableRowsDirtyReference(generateTableRowsTemplate(testResultRows));
          setIsLoading(false);
          selection.row = 0;
          selection.column = 0;
          focusOnHot();
        })
        .catch(handleHttpError)
        .finally(() => setIsLoading(false));
    }
  }, [testCase, cursor, open]);

  return (
    <Dialog open={open} maxWidth="lg" onClose={handleClose}>
      <CloseIcon className={classes.icon} onClick={handleClose} />
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <h2 className={classes.header}>{testSeriesObject && testSeriesObject.name}</h2>
        </Grid>
        <Grid container className={classes.rowInfo} item xs={12}>
          {headerArray.map((e, i) => {
            if (i === 0 && testCaseId === 0) {
              setTestCaseId(row[e.id]);
            }
            return (
              <span key={e.id}>
                {i !== 0 && ", "}
                <span className={classes.spanLabel}>{e.name}</span>
                {e.tooltip ? (
                  <BlackChipTooltip
                    noChip
                    section={e.tooltip.section}
                    subHeader={e.tooltip.subHeader && row[e.tooltip.subHeader]}
                    secondaryChips={row[e.tooltip.chipSelector]}
                  >
                    <b>{` ${row[e.id]}`}</b>
                  </BlackChipTooltip>
                ) : e.name === "ID" ? (
                  <b>{` ${testSeriesObject.indexNumber}-${row.indexNumber}`}</b>
                ) : (
                  <b>{` ${row[e.id]}`}</b>
                )}
              </span>
            );
          })}
        </Grid>
        <ErrorMessage ref={errorMessage} />
        <Grid item xs={12}>
          {isLoading ? (
            <div className={classes.stretch}>
              <CustomProgress />
            </div>
          ) : (
            <div>
              {tableRows && tableColumns && (
                <HotTable
                  ref={hot}
                  id="hot"
                  data={tableRows}
                  rowHeaders={false}
                  readOnly={readOnly}
                  rowHeights={32}
                  manualColumnResize
                  licenseKey="31e17-23a3c-2e0a0-94604-d6938"
                  width="1200"
                  height="300"
                  settings={hotSettings}
                  afterSelection={changeSelection}
                  beforeKeyDown={handleBeforeKeyDownEvent}
                  beforeOnCellMouseDown={beforeOnCellMouseDown}
                  afterOnCellMouseDown={handleAfterOnCellMouseDown}
                >
                  {generateHotColumns(tableColumns)}
                  <HotColumn title="Note" width={50} data="note">
                    <NoteEditorComponent hot-editor readOnly={readOnly} testCaseNumber={testCaseIndexNumber} />
                    <NoteRendererComponent hot-renderer readOnly={readOnly} />
                  </HotColumn>
                  <HotColumn title="Void" type="checkbox" data="voided" skipColumnOnPaste />
                  <HotColumn readOnly title="View" width={50} data="attachments">
                    <FileViewRendererComponent hot-renderer />
                  </HotColumn>
                  {!readOnly && (
                    <HotColumn title="Add" width={50}>
                      <FileUploadRendererComponent hot-renderer />
                    </HotColumn>
                  )}
                </HotTable>
              )}
            </div>
          )}
        </Grid>
        <Grid container alignItems="center" spacing={1}>
          <Grid container item xs={2}>
            {testSeriesObject.testSeriesStatus === "COMPLETED" ||
            testSeriesObject.testSeriesStatus === "CANCELED" ? null : (
              <CustomLinkButton disabled={isAddRowDisabled} addPlusSign onClick={addTableRow}>
                ADD ROW
              </CustomLinkButton>
            )}
          </Grid>
          <Grid container item xs={8} justify="flex-end">
            <TestCaseRatingRadioGroup
              disabled={completedOrCanceled}
              onChange={handleRatingChange}
              value={apiData?.rating || "NOT_RATED"}
            />
          </Grid>
          <Grid container item xs={2} justify="flex-end">
            <Box>
              <CustomButton disabled={isSaving || isLoading} text="<" onClick={handleLeftButtonClick} />
            </Box>
            <Box marginLeft={3}>
              <CustomButton disabled={isSaving || isLoading} primary text=">" onClick={handleRightButtonClick} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <AttachmentDialog
        open={showUploadDialog ? {} : null}
        header={`UPLOAD FILE TO REPETITION ${selection.row + 1} of TEST ID ${testCaseIndexNumber}`}
        onClose={() => {
          setShowUploadDialog(false);
          focusOnHot();
        }}
        onSubmit={(filesForUpload, description) => {
          // Check for confidential information in file names, description, and note
          const fileNames = filesForUpload.map((file) => file.name);
          const currentRowNote = tableRows[selection.row]?.note
            ? (JSON.parse(tableRows[selection.row].note) as { text: string }).text
            : "";
          const fieldsToCheck = [...fileNames, description, currentRowNote].filter(Boolean);
          
          if (confidentialRegex && containsHighlyConfidentialInfo(fieldsToCheck, confidentialRegex)) {
            setPendingUpload({ files: filesForUpload, description });
            setShowConfidentialDialog(true);
            setShowUploadDialog(false);
            return;
          }
          if (!apiData) return;
          const testResultId = apiData.id;
          const currentRow = selection.row;
          const testResultRowId = apiData.testResultRows[currentRow].id;
          uploadHelper.upload(
            filesForUpload.map((file) => {
              const isImage = file.type.startsWith("image/");
              const newName = `${filePrefix}_${currentRow + 1}_${isImage ? "Picture" : "Document"}`;
              const flag = isImage ? PICTURE : DOCUMENT;
              const name = FileNameHelper.renameFileWithEnding(newName, file.name);
              return {
                file,
                description,
                name,
                flag,
                notifications: {
                  finished: `Uploaded file ${name}.`,
                  error: `Failed to upload file ${name}.`,
                },
              };
            }),
            (attachment) =>
              testRequestService
                .addAttachmentToTestResultRow(
                  testResultId,
                  testResultRowId,
                  attachment.thumbnailsDisabled ? "DOCUMENT" : "PICTURE",
                  [attachment.uuid],
                )
                .then((attachments) => {
                  apiData.testResultRows[currentRow].attachments = attachments;
                  hot.current?.hotInstance?.setDataAtCell(currentRow, viewColumnIndex, true);
                }),
            showSnackbar,
          );
          setShowUploadDialog(false);
          focusOnHot();
        }}
        onAlert={showErrorSnackbar}
      />
      {showConfidentialDialogForSave && (
        <ConfidentialConfirmationDialog
          objectName="note"
          open={showConfidentialDialogForSave}
          onCancel={() => {
            setShowConfidentialDialogForSave(false);
            if (pendingSaveReject) {
              pendingSaveReject(new Error("User cancelled save due to confidential content"));
            }
            setPendingSaveResolve(null);
            setPendingSaveReject(null);
            focusOnHot();
          }}
          onProceed={() => {
            setShowConfidentialDialogForSave(false);
            
            testRequestService
              .updateConfidentiality(testRequestObj.requestId, "HIGHLY_CONFIDENTIAL")
              .then(() => {
                showSnackbar("Test request set to highly confidential", "success");
                
                if (apiData && (testSeriesStatus !== "COMPLETED" && testSeriesStatus !== "CANCELED")) {
                  const testResultRows = updateTestRowApiProp() || [];
                  setIsSaving(true);
                  return testRequestService
                    .updateTestResults({
                      ...apiData,
                      testResultRows,
                    })
                    .then(() => {
                      apiData.testResultRows = testResultRows;
                      if (pendingSaveResolve) {
                        pendingSaveResolve();
                      }
                    })
                    .catch((error) => {
                      if (pendingSaveReject) {
                        pendingSaveReject(error);
                      }
                    })
                    .finally(() => {
                      isDirty.current = false;
                      setIsSaving(false);
                      setPendingSaveResolve(null);
                      setPendingSaveReject(null);
                    });
                }
                if (pendingSaveResolve) {
                  pendingSaveResolve();
                  setPendingSaveResolve(null);
                  setPendingSaveReject(null);
                }
              })
              .catch((error) => {
                handleHttpError(error);
                if (pendingSaveReject) {
                  pendingSaveReject(error);
                  setPendingSaveResolve(null);
                  setPendingSaveReject(null);
                }
              });
          }}
        />
      )}
      {showConfidentialDialog && pendingUpload && (
        <ConfidentialConfirmationDialog
          objectName="attachment"
          open={showConfidentialDialog}
          onCancel={() => {
            setShowConfidentialDialog(false);
            setPendingUpload(null);
            setShowUploadDialog(true);
          }}
          onProceed={() => {
            if (!apiData || !pendingUpload) return;
            
            testRequestService
              .updateConfidentiality(testRequestObj.requestId, "HIGHLY_CONFIDENTIAL")
              .then(() => {
                ;
                
                const testResultId = apiData.id;
                const currentRow = selection.row;
                const testResultRowId = apiData.testResultRows[currentRow].id;
                const { files: filesForUpload, description } = pendingUpload;
                
                uploadHelper.upload(
                  filesForUpload.map((file) => {
                    const isImage = file.type.startsWith("image/");
                    const newName = `${filePrefix}_${currentRow + 1}_${isImage ? "Picture" : "Document"}`;
                    const flag = isImage ? PICTURE : DOCUMENT;
                    const name = FileNameHelper.renameFileWithEnding(newName, file.name);
                    return {
                      file,
                      description,
                      name,
                      flag,
                      notifications: {
                        finished: `Uploaded file ${name}.`,
                        error: `Failed to upload file ${name}.`,
                      },
                    };
                  }),
                  (attachment) =>
                    testRequestService
                      .addAttachmentToTestResultRow(
                        testResultId,
                        testResultRowId,
                        attachment.thumbnailsDisabled ? "DOCUMENT" : "PICTURE",
                        [attachment.uuid],
                      )
                      .then((attachments) => {
                        apiData.testResultRows[currentRow].attachments = attachments;
                        hot.current?.hotInstance?.setDataAtCell(currentRow, viewColumnIndex, true);
                      }),
                  showSnackbar,
                );
                setShowConfidentialDialog(false);
                setPendingUpload(null);
                focusOnHot();
              })
              .catch((error) => {
                handleHttpError(error);
                setShowConfidentialDialog(false);
                setPendingUpload(null);
              });
          }}
        />
      )}
      <AttachmentDetailDialog
        open={showViewDialog}
        onClose={() => {
          setShowViewDialog(false);
          focusOnHot();
        }}
        showIndex
        disabled={readOnly}
        fileHandler={attachmentFileHandler}
        namingStrategy={namingStrategy}
        images={images}
        onDelete={onDeleteImage}
        onDescriptionChange={onDescriptionChange}
      />
    </Dialog>
  );
}

export default forwardRef(TestCasesParametersTable);
